import React, { useEffect, useMemo, useState } from 'react';

const MIN_SCREEN_WIDTH = 900;
const MIN_SCREEN_HEIGHT = 675;
const DISMISS_TTL_MS = 12 * 60 * 60 * 1000;
const DISMISS_KEY = 'cv_small_screen_dismissed';

function getReportsHostname() {
  const fallback = 'reports.crawlervision.com';
  try {
    const raw = process.env.REACT_APP_REPORTS_URL || `https://${fallback}`;
    return new URL(raw).hostname.toLowerCase();
  } catch (_) {
    return fallback;
  }
}

function readDismissedAt() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.dismissedAt);
    return Number.isFinite(ts) ? ts : null;
  } catch (_) {
    return null;
  }
}

function saveDismissedAt(ts) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ dismissedAt: ts }));
  } catch (_) {
    // Ignore localStorage write failures and keep app usable.
  }
}

export default function SmallScreenNotice() {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : MIN_SCREEN_WIDTH,
    height: typeof window !== 'undefined' ? window.innerHeight : MIN_SCREEN_HEIGHT,
  }));
  const [dismissedAt, setDismissedAt] = useState(() => readDismissedAt());
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const reportsHostname = useMemo(getReportsHostname, []);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isReportPath = pathname.startsWith('/api/seo/report');
  const isReportsHost = hostname === reportsHostname;
  const isLearningPath =
    pathname === '/learning' ||
    pathname === '/learning-page' ||
    pathname.startsWith('/learning/');
  const isHomePath = pathname === '/' || pathname === '';
  const isPartnerPath = pathname === '/partner' || pathname.startsWith('/partner/');
  const isExcluded = isReportPath || isReportsHost || isLearningPath || isHomePath || isPartnerPath;
  const isSmallScreen = viewport.width < MIN_SCREEN_WIDTH;
  const isDismissedWithinTtl =
    Number.isFinite(dismissedAt) && (Date.now() - dismissedAt) < DISMISS_TTL_MS;
  const showStrip = !isExcluded && isSmallScreen && isDismissedWithinTtl;

  useEffect(() => {
    const onResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const updateScrollState = () => {
      setIsScrolled(window.scrollY > 8);
    };
    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  useEffect(() => {
    if (isExcluded) {
      setIsPopupOpen(false);
      return;
    }
    if (isSmallScreen && !isDismissedWithinTtl) {
      setIsPopupOpen(true);
      return;
    }
    if (!isSmallScreen) {
      setIsPopupOpen(false);
    }
  }, [isExcluded, isSmallScreen, isDismissedWithinTtl]);

  const closePopup = () => {
    const now = Date.now();
    setDismissedAt(now);
    saveDismissedAt(now);
    setIsPopupOpen(false);
  };

  if (isExcluded || (!showStrip && !isPopupOpen)) {
    return null;
  }

  return (
    <>
      {showStrip && (
        <div className={isScrolled ? 'fixed top-[calc(env(safe-area-inset-top)+72px)] left-0 right-0 z-[45] px-3 sm:px-4 pt-2' : 'w-full px-3 sm:px-4 mt-2'}>
          <div className="max-w-7xl mx-auto">
            <button
              type="button"
              onClick={() => setIsPopupOpen(true)}
              className="w-full bg-cv-gold/35 border-2 border-cv-gold text-cv-navy px-4 py-3 text-sm font-semibold text-left shadow-md rounded-xl"
              aria-label="Small-screen warning. Click to view details."
            >
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cv-gold text-white text-xs font-bold">!</span>
                <span>Warning: CrawlerVision is not optimized for small screens. Tap to view minimum screen requirements.</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {isPopupOpen && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-xl rounded-2xl bg-cv-gold/15 shadow-2xl border-2 border-cv-gold/50">
            <div className="px-6 py-5 border-b border-cv-gold/50 bg-cv-gold/25 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-cv-gold text-white text-lg font-bold">!</span>
                <div>
                  <p className="text-xs font-bold tracking-wide uppercase text-cv-navy">Screen Size Warning</p>
                  <h2 className="text-xl font-bold text-cv-navy">CrawlerVision is built for larger screens</h2>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 space-y-3 text-cv-navy">
              <p>
                This experience is not optimized for phone-sized displays.
              </p>
              <p>
                Please use at least <strong>{MIN_SCREEN_WIDTH}x{MIN_SCREEN_HEIGHT}</strong> resolution or a screen bigger than <strong>12 inches</strong>.
              </p>
              <p>
                Recommended devices: Desktop, Laptop, Samsung Dex, iPad, or Android Tablet.
              </p>
            </div>

            <div className="px-6 py-4 bg-cv-gold/25 rounded-b-2xl flex justify-end border-t border-cv-gold/50">
              <button
                type="button"
                onClick={closePopup}
                className="px-4 py-2 bg-cv-gold text-white font-semibold rounded-lg hover:bg-cv-gold transition-colors"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
